import { useState } from 'react'
import { useAuthStore } from '../../store/authStore'
import { useChatThemeStore } from '../../store/chatThemeStore'
import { 
  PhoneIcon, 
  VideoCameraIcon, 
  InformationCircleIcon,
  EllipsisVerticalIcon,
  SwatchIcon
} from '@heroicons/react/24/outline'

const ChatHeader = ({ chat }) => {
  const [showMenu, setShowMenu] = useState(false)
  const { user } = useAuthStore()
  const { toggleThemePicker } = useChatThemeStore()

  const getChatDisplayName = () => {
    if (!chat) return 'Chat'
    
    if (chat.isGroupChat) {
      return chat.name || 'Group Chat'
    }
    
    // For direct chats, show the other participant's name
    const otherParticipant = chat.participants?.find(p => p._id !== user?._id)
    return otherParticipant?.username || 'Unknown User'
  }

  const getChatAvatar = () => {
    if (!chat) return null
    
    if (chat.isGroupChat) {
      return chat.avatar || null
    }
    
    const otherParticipant = chat.participants?.find(p => p._id !== user?._id)
    return otherParticipant?.avatar || null
  }

  const getOnlineStatus = () => {
    if (!chat || chat.isGroupChat) return null
    
    const otherParticipant = chat.participants?.find(p => p._id !== user?._id)
    return otherParticipant?.isOnline ? 'Online' : 'Offline'
  }

  const displayName = getChatDisplayName()
  const avatar = getChatAvatar()
  const onlineStatus = getOnlineStatus()

  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      {/* Left side - Chat info */}
      <div className="flex items-center space-x-3">
        {/* Avatar */}
        <div className="flex-shrink-0 relative">
          {avatar ? (
            <img
              src={avatar}
              alt={displayName}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              {chat?.isGroupChat ? (
                <svg className="w-5 h-5 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.196-2.12M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.12M7 20v-2c0-.656.126-1.283.356-1.857M9 9a3 3 0 116 0 3 3 0 01-6 0z" />
                </svg>
              ) : (
                <span className="text-primary-600 dark:text-primary-400 font-medium">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          )}
          
          {/* Online indicator for direct chats */}
          {!chat?.isGroupChat && onlineStatus === 'Online' && (
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
          )}
        </div>

        {/* Name and status */}
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
            {displayName}
          </h2>
          {onlineStatus && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {onlineStatus}
            </p>
          )}
          {chat?.isGroupChat && chat.participants && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {chat.participants.length} members
            </p>
          )}
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center space-x-2">
        {/* Call buttons (for direct chats) */}
        {!chat?.isGroupChat && (
          <>
            <button
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Voice call"
            >
              <PhoneIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
            
            <button
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Video call"
            >
              <VideoCameraIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </button>
          </>
        )}

        {/* Theme button */}
        <button
          onClick={toggleThemePicker}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Change chat theme"
        >
          <SwatchIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>

        {/* Info button */}
        <button
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          title="Chat info"
        >
          <InformationCircleIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <EllipsisVerticalIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          </button>

          {showMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setShowMenu(false)}
              />
              
              {/* Menu */}
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                <div className="py-1">
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    Search in conversation
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    Mute notifications
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                    Clear history
                  </button>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                  <button className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                    {chat?.isGroupChat ? 'Leave group' : 'Delete chat'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ChatHeader